type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';
type MessageHandler = (message: any) => void;
type StatusListener = (status: ConnectionStatus) => void;

class FamhearthWebSocket {
  private ws: WebSocket | null = null;
  private handlers: Map<string, MessageHandler[]> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private shouldReconnect = false;
  private token: string | null = null;
  private _connectionStatus: ConnectionStatus = 'disconnected';
  private statusListeners: Set<StatusListener> = new Set();

  get connectionStatus(): ConnectionStatus { return this._connectionStatus; }

  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private _setStatus(status: ConnectionStatus) {
    this._connectionStatus = status;
    this.statusListeners.forEach(l => l(status));
  }

  connect(token: string): void {
    // If already connected with same token, skip
    if (this.token === token && this.ws?.readyState === WebSocket.OPEN) return;
    this.token = token;
    this.shouldReconnect = true;
    this.reconnectDelay = 1000;
    this._connect();
  }

  // Update the token and reconnect (used after proactive token refresh)
  updateToken(token: string): void {
    this.token = token;
    // If connected, close and reconnect with new token
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close();
      // onclose handler will trigger reconnect with the new token
    }
  }

  private _connect(): void {
    if (this.ws) {
      this.ws.close();
    }

    const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3001';
    // The JWT used to be passed as ?token=... in the URL — bad practice
    // because proxies, CDNs, and browser history all log URLs (M-13). We
    // now open the socket without a credential and authenticate via a
    // first-message handshake.
    const url = `${WS_URL}/ws`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.reconnectDelay = 1000;
      // Server expects AUTH as the first message; it has 10 seconds to
      // receive one or it drops the connection.
      if (this.token && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'AUTH', token: this.token }));
      }
      this._setStatus('connected');
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this._dispatch(message.type, message);
        this._dispatch('*', message);
      } catch {
        // ignore
      }
    };

    this.ws.onclose = () => {
      // disconnected
      this._setStatus(this.shouldReconnect ? 'reconnecting' : 'disconnected');
      if (this.shouldReconnect) {
        const delay = this.reconnectDelay;
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
        this.reconnectTimer = setTimeout(() => {
          this._connect();
        }, delay);
      }
    };

    this.ws.onerror = (error) => {
      // WebSocket error — reconnect will be attempted
    };
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this._setStatus('disconnected');
  }

  on(eventType: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, []);
    }
    this.handlers.get(eventType)!.push(handler);

    return () => {
      const handlers = this.handlers.get(eventType) || [];
      this.handlers.set(eventType, handlers.filter(h => h !== handler));
    };
  }

  private _dispatch(eventType: string, message: any): void {
    const handlers = this.handlers.get(eventType) || [];
    handlers.forEach(h => h(message));
  }

  send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }
}

export const wsClient = new FamhearthWebSocket();
